/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useSessionContext } from '@exo/frontend-common-session-context';
import { useTokenContext } from '@exo/frontend-common-app-identity-and-access';

const REFETCH_LINE_ITEMS = gql`
  fragment useCartModificationRefetchLineItems on CrtCart {
    id
    lineItems {
      id
      quantity
      partnumber
      linePrice {
        currency
        value
      }
      product {
        availability {
          availableQuantity
        }
      }
    }
  }
`;

const REFETCH_PRICES = gql`
  fragment useCartModificationRefetchPrices on CrtCart {
    id
    grandTotal {
      value
      currency
    }
    totalProductPrice {
      value
      currency
    }
    totalShippingCharge {
      value
      currency
    }
    totalAdjustment {
      value
      currency
    }
    totalShippingTax {
      value
      currency
    }
    totalSalesTax {
      value
      currency
    }
    adjustments {
      title
      amount {
        currency
        value
      }
    }
  }
`;

const REFETCH_DELIVERY_OPTIONS = gql`
  fragment useCartModificationRefetchDeliveryOptions on CrtCart {
    id
    availableShippingModes {
      id
      identifier
      description
      type
      shippingRate {
        value
        currency
      }
    }
    shippingInfo {
      shipMode {
        id
      }
      pickupInStore {
        id
        name
      }
    }
  }
`;

const GQL_ADD = gql`
  mutation AddToCart($lineItems: [CrtLineItemInput!]!, $cartId: ID) {
    cartAdd(cartId: $cartId, lineItems: $lineItems) {
      id
      ...useCartModificationRefetchLineItems
      ...useCartModificationRefetchDeliveryOptions
      ...useCartModificationRefetchPrices
    }
  }

  ${REFETCH_LINE_ITEMS}
  ${REFETCH_DELIVERY_OPTIONS}
  ${REFETCH_PRICES}
`;

const GQL_UPDATE = gql`
  mutation UpdateCart($lineItems: [CrtLineItemForUpdateInput!]!, $cartId: ID!) {
    cartUpdate(cartId: $cartId, lineItems: $lineItems) {
      id
      ...useCartModificationRefetchLineItems
      ...useCartModificationRefetchDeliveryOptions
      ...useCartModificationRefetchPrices
    }
  }

  ${REFETCH_LINE_ITEMS}
  ${REFETCH_DELIVERY_OPTIONS}
  ${REFETCH_PRICES}
`;

const GQL_SET_SHIPPING_MODE = gql`
  mutation CartUpdateShippingInfo($storeId: ID, $addressId: ID, $shipModeId: ID, $cartId: ID!) {
    cartSetShippingInfo(
      cartId: $cartId
      storeId: $storeId
      addressId: $addressId
      shipModeId: $shipModeId
      lineItemIds: []
    )
  }
`;

const GQL_UPDATE_PAYMENT_METHOD = gql`
  mutation CartUpdatePaymentMethod(
    $addressId: ID
    $cartId: ID!
    $address: CusAddressInput
    $paymentMethodId: ID
    $attributes: [CrtPaymentAttributeInput]
  ) {
    cartAddPaymentInstruction(
      cartId: $cartId
      addressId: $addressId
      address: $address
      paymentMethodId: $paymentMethodId
      attributes: $attributes
    )
  }
`;

const GQL_DELETE_PAYMENT_METHODS = gql`
  mutation CartClearPaymentMethods($cartId: ID!) {
    cartDeletePaymentInstructions(cartId: $cartId)
  }
`;

const GQL_APPLYPROMOCODE = gql`
  mutation ApplyPromotionCode($promoCode: String!) {
    applyPromotionCode(promoCode: $promoCode) {
      orderId
      promoCode
    }
  }
`;
const GQL_REMOVEPROMOCODE = gql`
  mutation RemovePromotionCode($promoCode: String!) {
    removePromotionCode(promoCode: $promoCode)
  }
`;
const GQL_CHECKOUT = gql`
  mutation Checkout($id: ID!) {
    checkout(cartId: $id)
  }
`;

export const GQL_UPDATE_SHIPPING_ADDRESS = gql`
  mutation CartSetShippingInfo(
    $cartId: ID!
    $addressId: ID
    $address: CusAddressInput
    $shipModeId: ID
  ) {
    cartSetShippingInfo(
      cartId: $cartId
      addressId: $addressId
      address: $address
      shipModeId: $shipModeId
    )
  }
`;
export const GQL_VALIDATE_PURCHASE_LIMIT = gql`
  query ValidatePurchaseLimit($orderId: String!, $fromPage: FromPage!) {
    validatePurchaseLimit(orderId: $orderId, fromPage: $fromPage) {
      maximumPurchaseLimitPerOrder
      isWithinPurchaseLimits
      errorMessageKey
      errorMessage
    }
  }
`;
export const GQL_UPDATE_BILLING_ADDRESS = gql`
  mutation CartSetBillingInfo($cartId: ID!, $address: CusAddressInput) {
    cartAddPaymentInstruction(cartId: $cartId, address: $address)
  }
`;

const GQL_CREATE_CART = gql`
  mutation CreateCart($oldCartId: ID, $currency: String) {
    cartCreate(oldCartId: $oldCartId, currency: $currency) {
      id
    }
  }
`;

const RECALCULATE_PRICE_GQL = gql`
  query RecalculateCart($cartId: String, $updatePrices: Boolean, $checkoutType: String, $calculateParams: CalculateParams) {
  recalculateCart(cartId: $cartId, updatePrices: $updatePrices, checkoutType: $checkoutType, calculateParams: $calculateParams) {
    cartId
    success
  }
}
`;

export const GQL_GET_SKU = gql`
  query CartGetSku($id: String) {
    product(id: $id) {
      id
      type
      children {
        id
        type
      }
    }
  }
`;

const GQL_START_CHECKOUT = gql`
mutation StartCheckout {
  startCheckout {
    isPriceRefreshed
    orderId
  }
}
`;

const CHECK_PRODUCT_AVAILABILITY = gql`
   query CartById($id: ID) {
  me {
    id
    carts(cartId: $id) {
      id
      lineItems {
       quantity
        product {
        availability {
          availableQuantity
        }
      }
      }
    }
  }
}
        
`;

const GET_APPLID_PROMOCODE = gql`
   query GetAppliedPromotionCodesOnCart {
  getAppliedPromotionCodesOnCart {
    orderId
    promotionCode {
      code
    }
  }
}
        
`;

const GET_CART_ID =  gql 
`query CartById_Refetch($id: ID) {
  me {
    id
    carts(cartId: $id) {
      id
    }
  }
}
`
type GetSkuResponse = {
  data?: {
    product?: {
      id: string;
      type: string;
      children?: {
        id: string;
        type: string;
      };
    };
  };
};

const refresh = (id: string) => ({
  refetchQueries: [
    {
      query: gql`
        query CartById_Refetch($id: ID) {
          me {
            id
            carts(cartId: $id) {
              id
              ...useCartModificationRefetchLineItems
              ...useCartModificationRefetchDeliveryOptions
              ...useCartModificationRefetchPrices
            }
          }
        }
        ${REFETCH_LINE_ITEMS}
        ${REFETCH_DELIVERY_OPTIONS}
        ${REFETCH_PRICES}
      `,
      variables: { id }
    }
  ]
});

const DELETE_BANK_NOTE_FROM_CART = gql`
mutation DeleteBankNoteFromCart($itemInput: BankNoteDeleteInput!) {
  deleteBankNoteFromCart(itemInput: $itemInput) {
    orderId
  }
}
`;
const UPDATE_BANK_NOTE_FROM_CART = gql`
mutation CartUpdate($itemInput: BankNoteUpdateInput!) {
  updateBankNoteInCart(itemInput: $itemInput) {
    id
    orderId
  }
}
`;

const GQL_MIXED_CART_CHECK = gql`
mutation PmMixedCartCheck {
  pmMixedCartCheck {
    hasPhysicalProducts
    hasPooledProducts
  }
}
`;

const GQL_TRACK_ABANDONED_CART = gql`
mutation TrackAbandonedCart($fromPage: String!) {
  trackAbandonedCart(fromPage: $fromPage) {
    orderId
  }
}
`;

export const useCartModification = (props?: Props) => {
  const apolloClient = useApolloClient();
  const session = useSessionContext();
  const tokenContext = useTokenContext();
  let cartId = session?.cartId;

  const updateQty = (key: string) => d => {
    const newCartCount = d[key].lineItems.map(li => li.quantity).reduce((a, b) => a + b, 0);
    session.set({ cartCount: newCartCount, cartId: d[key].id });
  };
  const [gqlAdd] = useMutation(GQL_ADD, { onCompleted: updateQty('cartAdd') });
  const [gqlUpdate] = useMutation(GQL_UPDATE, { onCompleted: updateQty('cartUpdate') });
  const [gqlSetShippingMode] = useMutation(GQL_SET_SHIPPING_MODE, refresh(cartId!));
  const [gqlUpdatePaymentMethod] = useMutation(GQL_UPDATE_PAYMENT_METHOD, refresh(cartId!));
  const [gqlDeletePaymentMethods] = useMutation(GQL_DELETE_PAYMENT_METHODS, refresh(cartId!));
  const [gqlCheckout] = useMutation(GQL_CHECKOUT);
  const [gqlUpdateShippingAddress] = useMutation(GQL_UPDATE_SHIPPING_ADDRESS, refresh(cartId!));
  const [gqlUpdateBillingAddress] = useMutation(GQL_UPDATE_BILLING_ADDRESS, refresh(cartId!));
  const [gqlCreateCart] = useMutation(GQL_CREATE_CART);
  const [gqlApplyPromocode] = useMutation(GQL_APPLYPROMOCODE, refresh(cartId!));
  const [gqlRemovePromocode] = useMutation(GQL_REMOVEPROMOCODE, refresh(cartId!));
  const [gqldeleteBankNoteFromCart] = useMutation(DELETE_BANK_NOTE_FROM_CART, refresh(cartId!));
  const [gqlupdateBankNoteFromcart]=useMutation(UPDATE_BANK_NOTE_FROM_CART);
  const [gqlMixedCartCheck] = useMutation(GQL_MIXED_CART_CHECK);
  const [gqlStartCheckout] = useMutation(GQL_START_CHECKOUT);
  const [gqlTrackAbandonedCart] = useMutation(GQL_TRACK_ABANDONED_CART);


  return {
    add: async (items, currency?: string) => {
      await tokenContext.ensureValidToken();

      if (!session.cartId) {
        const res = await apolloClient.query({
          query: GET_CART_ID,
          variables: { cartId },
          fetchPolicy: 'network-only'
        });
        if(res.data.me.carts.length > 0 && !(session.cartId)){
            session.set({cartId:res?.data?.me.carts[0].id})
            cartId = res?.data?.me.carts[0].id
        }
        else{
          const res = await gqlCreateCart({ variables: { currency } });
          session.set({cartId:res?.data?.cartCreate?.id})
          cartId = res?.data?.cartCreate?.id;
        } 
      }

      const effectiveItems = [...(Array.isArray(items) ? items : [items])];
      if (props?.guessSkus) {
        if (items.length > 1)
          throw new Error('SKU resolution for addToCart only implemented for individual items');
        const skuResponse = (await apolloClient.query({
          query: GQL_GET_SKU,
          variables: { id: items[0].id }
        })) as GetSkuResponse;

        if (skuResponse.data?.product?.type === 'product') {
          effectiveItems[0].id = skuResponse.data?.product?.children?.[0]?.id;
          delete effectiveItems[0].partnumber;
        }
      }

      return gqlAdd({ variables: { cartId, lineItems: effectiveItems } });
    },

    update: async items => {
      return gqlUpdate({ variables: { lineItems: items, cartId } });
    },

    setShippingMode: async (shipModeId, addressId, storeId) => {
      return gqlSetShippingMode({ variables: { storeId, addressId, shipModeId, cartId } });
    },

    updateShippingAddress: async (address, shipModeId?) =>
      gqlUpdateShippingAddress({
        variables: {
          cartId,
          address: Object.fromEntries(Object.entries(address).filter(([key]) => key !== 'id')),
          addressId: address?.id,
          shipModeId
        }
      }),

    updateBillingAddress: async address => {
      await gqlDeletePaymentMethods({ variables: { cartId } });
      gqlUpdateBillingAddress({ variables: { cartId, address } });
    },

    setPaymentMethod: async (addressId, address, paymentMethodId, attributes) => {
      await gqlDeletePaymentMethods({ variables: { cartId } });
      return gqlUpdatePaymentMethod({
        variables: {
          addressId,
          address,
          cartId,
          paymentMethodId,
          attributes: attributes ?? []
        }
      });
    },

    checkout: async (id: string) => gqlCheckout({ variables: { id: id || -1 } }),

    createCart: async ({ oldCartId, currency }: { oldCartId?: string; currency?: string }) => {
      const res = await gqlCreateCart({
        variables: { oldCartId, currency }
      });
      return res?.data?.cartCreate?.id;
    },
    applyPromocode: async (promoCode: string) => {
      try {
        const res = await gqlApplyPromocode({ variables: { promoCode } });
        return res?.data;
      } catch (err: any) {
        return err;
      }
    },
    removePromocode: async (promoCode: string) => {
      await gqlRemovePromocode({ variables: { promoCode } });
    },
    appliedPromocode: async () => {
      try {
        const res = await apolloClient.query({
          query: GET_APPLID_PROMOCODE,
          fetchPolicy: 'network-only'
        });
        return res?.data;
      } catch (err: any) {
        return err;
      }
    },
    validatePurchaseLimit: async (orderId: string, fromPage: string) => {
      const cartId = orderId ? orderId : ""
      const res = await apolloClient.query({
        query: GQL_VALIDATE_PURCHASE_LIMIT,
        variables: { orderId:cartId , fromPage:fromPage },
        fetchPolicy: 'network-only'
      });
      return res;
    },
    mixedCartCheck: async () => {
      const res = await gqlMixedCartCheck({
        fetchPolicy: 'network-only'
      });
      return res?.data?.pmMixedCartCheck
    },
    startCheckout: async () => {
      const res = await gqlStartCheckout()
      return res?.data
    },
    TrackAbandonedCart: async (fromPage) => {
      const res = await gqlTrackAbandonedCart({
        variables: {
          fromPage: fromPage
        }
      });
      return res?.data?.trackAbandonedCart?.orderId;
    },
    reCalculateCart: async (cartId: string, updatePrices: boolean, checkoutType: string, calculateParams) => {
      const res = await apolloClient.query({
        query: RECALCULATE_PRICE_GQL,
        variables: { cartId, updatePrices, checkoutType, calculateParams },
        fetchPolicy: 'network-only'
      });
      return res;
    },
    deleteItemfromBankNote: async itemInput => {
      const res = await gqldeleteBankNoteFromCart({ variables: { itemInput } });
      return res;
    },
    updateItemfromBankNote:async itemInput =>{
      const res =await gqlupdateBankNoteFromcart({variables:{itemInput}});
      return res;
    },
    checkCartAvaibility: async (orderId: string) => {
      const res = await apolloClient.query({
        query: CHECK_PRODUCT_AVAILABILITY,
        variables: { orderId },
        fetchPolicy: 'network-only'
      });
      return res;
    },
    setCartId: async () => {
      if (!session.cartId) {
        const res = await apolloClient.query({
          query: GET_CART_ID,
          variables: { cartId },
          fetchPolicy: 'network-only'
        });
        if(res.data.me.carts.length > 0){
          return res?.data?.me.carts[0].id
        }
      }

    }
  };
};

type Props = { guessSkus?: boolean };
